import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { GoogleLogo, EnvelopeSimple, ShieldWarning } from "@phosphor-icons/react";
import { http } from "@/lib/api";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { login, signup, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login"); // or "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  // Sprint 60.31 — DFS legality gate. Preload on mount so the signup
  // form knows if this IP is in a blocked state (renders "not available"
  // instead of the form). min_age drives copy (18+ vs 21+ in MA/IA/LA).
  const [legality, setLegality] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  // Sprint 60.45 — Terms + Privacy + Play Responsibly gates. All three
  // must be affirmatively checked before signup succeeds (backend also
  // enforces so a tampered client is still rejected).
  const [tosConfirmed, setTosConfirmed] = useState(false);
  const [rgConfirmed, setRgConfirmed] = useState(false);

  useEffect(() => {
    let alive = true;
    http.get("/auth/legality-check")
      .then((r) => alive && setLegality(r.data))
      .catch(() => alive && setLegality({ allowed: true, min_age: 18, reason: "check_failed" }));
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        if (legality && !legality.allowed) {
          toast.error(legality.message || "Paid DFS is not available in your state.");
          setBusy(false); return;
        }
        if (!ageConfirmed) {
          toast.error(`Please confirm you meet the minimum age (${legality?.min_age || 18}+) to sign up.`);
          setBusy(false); return;
        }
        if (!tosConfirmed) {
          toast.error("Please accept the Terms of Service and Privacy Policy to continue.");
          setBusy(false); return;
        }
        if (!rgConfirmed) {
          toast.error("Please acknowledge the Play Responsibly disclosures to continue.");
          setBusy(false); return;
        }
        const res = await signup(
          email, password, name, inviteCode.trim() || undefined,
          {
            age_verified: true,
            terms_accepted: true,
            privacy_accepted: true,
            responsible_gambling_ack: true,
          },
        );
        if (res?.beta_activated) {
          toast.success("Beta unlocked — unlimited access activated.");
          nav("/app");
        } else {
          toast.success("Account created. Choose your plan.");
          nav("/pricing");
        }
      } else {
        await login(email, password);
        toast.success("Signed in.");
        nav("/app");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-8 justify-center">
          <img src="/brand/helmet_transparent.png" alt="WarPaint" className="w-10 h-10 object-contain" />
          <div className="heading font-black tracking-tighter uppercase text-xl">WarPaint DFS</div>
        </Link>

        <div className="bg-[#121212] border border-zinc-800 p-6">
          <div className="flex gap-1 mb-6">
            <button
              data-testid="tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 py-2 text-xs uppercase tracking-widest ${mode === "login" ? "bg-[#39FF14] text-black font-bold" : "border border-zinc-800 text-zinc-400"}`}
            >
              Sign In
            </button>
            <button
              data-testid="tab-signup"
              onClick={() => setMode("signup")}
              className={`flex-1 py-2 text-xs uppercase tracking-widest ${mode === "signup" ? "bg-[#39FF14] text-black font-bold" : "border border-zinc-800 text-zinc-400"}`}
            >
              Create Account
            </button>
          </div>

          <button
            data-testid="google-login"
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-2 py-3 bg-white text-black font-semibold hover:bg-zinc-100 mb-4"
          >
            <GoogleLogo size={18} weight="bold" />
            Continue with Google
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800" /></div>
            <div className="relative flex justify-center text-[10px] text-zinc-500 mono uppercase tracking-widest">
              <span className="bg-[#121212] px-3">or with email</span>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input
                data-testid="input-name"
                type="text" required placeholder="Warrior name"
                value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2.5 text-sm"
              />
            )}
            <input
              data-testid="input-email"
              type="email" required placeholder="Email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2.5 text-sm"
            />
            <input
              data-testid="input-password"
              type="password" required placeholder="Password" minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-2.5 text-sm"
            />
            {mode === "login" && (
              <div className="text-right -mt-2">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-[11px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14]"
                >
                  Forgot password?
                </Link>
              </div>
            )}
            {mode === "signup" && (
              showInvite ? (
                <div className="space-y-1">
                  <input
                    data-testid="input-invite-code"
                    type="text"
                    placeholder="Beta invite code"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    className="w-full bg-black border border-[#39FF14]/40 focus:border-[#39FF14] outline-none px-3 py-2.5 text-sm mono tracking-wider"
                  />
                  <div className="text-[10px] mono text-zinc-500 tracking-widest uppercase">
                    Valid codes unlock unlimited access · Case-insensitive
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="toggle-invite-code"
                  onClick={() => setShowInvite(true)}
                  className="text-[10px] mono text-[#39FF14] tracking-widest uppercase hover:text-[#2ee30d] cursor-pointer"
                >
                  + Have a beta invite code?
                </button>
              )
            )}
            {mode === "signup" && legality && !legality.allowed && (
              <div
                data-testid="legality-blocked-banner"
                className="border border-red-500/60 bg-red-500/10 p-3 flex items-start gap-2"
              >
                <ShieldWarning size={18} className="text-red-400 shrink-0 mt-0.5" />
                <div className="text-[11px] text-red-300 leading-relaxed">
                  <div className="font-bold mb-1 uppercase tracking-widest text-[10px]">
                    Not Available in Your State
                  </div>
                  {legality.message || "Paid DFS contests are not available under current state law."}
                  {legality.region_code && (
                    <div className="mt-1 text-zinc-500 mono text-[9px]">
                      Detected region: {legality.region_code}
                    </div>
                  )}
                </div>
              </div>
            )}
            {mode === "signup" && legality?.allowed && (
              <label
                data-testid="age-verify-checkbox-wrap"
                className="flex items-start gap-2 text-[11px] text-zinc-400 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  data-testid="age-verify-checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  className="mt-0.5 accent-[#39FF14]"
                />
                <span>
                  I certify I am at least{" "}
                  <strong className="text-white">{legality.min_age || 18}+</strong>
                  {" "}years old and legally allowed to enter paid daily fantasy contests
                  in my state.
                </span>
              </label>
            )}
            {mode === "signup" && legality?.allowed && (
              <label
                data-testid="tos-checkbox-wrap"
                className="flex items-start gap-2 text-[11px] text-zinc-400 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  data-testid="tos-checkbox"
                  checked={tosConfirmed}
                  onChange={(e) => setTosConfirmed(e.target.checked)}
                  className="mt-0.5 accent-[#39FF14]"
                />
                <span>
                  I have read and accept the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-[#39FF14] hover:underline">Terms of Service</a>
                  {" "}and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#39FF14] hover:underline">Privacy Policy</a>.
                </span>
              </label>
            )}
            {mode === "signup" && legality?.allowed && (
              <label
                data-testid="rg-checkbox-wrap"
                className="flex items-start gap-2 text-[11px] text-zinc-400 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  data-testid="rg-checkbox"
                  checked={rgConfirmed}
                  onChange={(e) => setRgConfirmed(e.target.checked)}
                  className="mt-0.5 accent-[#FFCC00]"
                />
                <span>
                  I understand DFS carries substantial risk of financial loss and have
                  reviewed the{" "}
                  <a href="/responsible-gambling" target="_blank" rel="noreferrer" className="text-[#FFCC00] hover:underline">Play Responsibly</a>
                  {" "}disclosures. If gambling harms me, I'll call{" "}
                  <a href="tel:1-800-426-2537" className="text-[#FFCC00] hover:underline">1-800-GAMBLER</a>.
                </span>
              </label>
            )}
            <button
              data-testid="submit-auth"
              type="submit" disabled={busy || (mode === "signup" && (!legality?.allowed || !ageConfirmed || !tosConfirmed || !rgConfirmed))}
              className="w-full py-3 bg-[#39FF14] text-black hover:bg-[#2ee30d] disabled:opacity-40 uppercase tracking-widest text-xs font-black"
            >
              <EnvelopeSimple size={14} weight="bold" className="inline mr-2" />
              {busy ? "…" : mode === "signup" ? "Create Account" : "Sign In"}
            </button>
          </form>

          {params.get("error") && (
            <div className="mt-4 text-xs text-[#FF3B30]">Authentication failed. Please try again.</div>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-zinc-500">
          <Link to="/" className="hover:text-white">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
